import { useRef } from "react";
import { ScrollReveal } from "@/hooks/useScrollReveal";
import { newBooks2026 } from "@/mocks/newBooks2026";
import { CATALOG_URL } from "@/mocks/site-config";
import { getBookPublicationBadge, getBookPublicationBadgeClass, getBookPublicationBadgeLabel } from "../../../../../shared/book-publication-status";

export default function NewBooks2026() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -320, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 320, behavior: "smooth" });
    }
  };

  return (
    <section className="min-h-[100svh] flex items-center bg-background-100 relative z-[2]">
      <div className="mx-auto px-6 md:px-10 py-16 md:py-24 max-w-[1120px] w-full">
        <ScrollReveal>
          <div className="flex flex-col lg:flex-row lg:gap-16 mb-10 md:mb-14">
            <div className="lg:w-[30%] flex-shrink-0 mb-6 lg:mb-0">
              <p className="text-xs tracking-[0.2em] uppercase text-foreground-400 font-medium">
                2026 New Releases
              </p>
            </div>
            <div className="lg:w-[70%]">
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
                2026 最新發行
              </h2>
              <p className="text-sm text-foreground-500 mt-4 leading-relaxed max-w-lg">
                查看崧燁文化於 2026 年最新發行的醫療保健、研究與專業知識書籍。
              </p>
            </div>
          </div>
        </ScrollReveal>

        <div className="hidden lg:flex justify-end gap-2 mb-6">
          <button
            onClick={scrollLeft}
            aria-label="向左滑動"
            className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300/60 text-foreground-600 hover:bg-background-200/60 hover:text-foreground-900 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground-400"
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </button>
          <button
            onClick={scrollRight}
            aria-label="向右滑動"
            className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300/60 text-foreground-600 hover:bg-background-200/60 hover:text-foreground-900 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground-400"
          >
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-5 md:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {newBooks2026.map((book, index) => (
            <ScrollReveal key={book.isbn} delay={index * 100}>
              <a
                href={CATALOG_URL}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="snap-start flex-shrink-0 w-[260px] md:w-[280px] group cursor-pointer block"
              >
                <div className="bg-white border border-background-200/60 p-3 md:p-4 mb-4 group-hover:border-background-300/80 transition-colors duration-200">
                  <div className="relative aspect-[3/4] w-full overflow-hidden bg-background-100">
                    <img
                      src={book.coverImage}
                      alt={`${book.title} 書封`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                    {(() => {
                      const badge = getBookPublicationBadge(book.publishDate);
                      return badge ? (
                        <span className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-xs font-medium ${getBookPublicationBadgeClass(badge)}`}>
                          {getBookPublicationBadgeLabel(badge)}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </div>
                <div className="px-1">
                  <p className="text-[11px] text-foreground-400 font-medium tracking-wider mb-1.5">
                    {book.code}
                  </p>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 leading-snug mb-1 group-hover:text-accent-600 transition-colors">
                    {book.title}
                  </h3>
                  {book.subtitle ? (
                    <p className="text-sm text-foreground-600 leading-snug mb-2">
                      {book.subtitle}
                    </p>
                  ) : null}
                  <p className="text-sm text-foreground-500">
                    {book.author}
                  </p>
                  <p className="text-[11px] text-foreground-400 mt-2 tracking-wide">
                    ISBN {book.isbn}
                  </p>
                  <span className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-accent-600 group-hover:text-accent-700 transition-colors whitespace-nowrap">
                    查看書籍資訊
                    <i className="ri-arrow-right-line text-[10px]"></i>
                  </span>
                </div>
              </a>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal>
          <div className="mt-14 md:mt-18 pt-8 border-t border-background-200/60">
            <p className="text-sm text-foreground-500 leading-relaxed max-w-lg mb-5">
              完整書名、作者及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
            </p>
            <a
              href={CATALOG_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 bg-foreground-900 hover:bg-foreground-800 text-white px-6 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground-400 focus-visible:ring-offset-2"
            >
              前往書籍資料庫
              <i className="ri-external-link-line text-sm"></i>
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
